import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMockData } from '../../../../contexts/MockDataContext';
import {
  addGatewayTopupRequest,
  addOfflineTopupRequest,
  addMockCompletedTickets,
} from '../../../../utils/mockWalletData';
import { WalletDepositAPI } from '../../../../services/walletService';
import AppService from '../../../../Configurations/Appservice';
import API_ENDPOINTS from '../../../../Api/endpoints';

// Step Components
import AmountStep from './steps/AmountStep';
import PaymentTypeStep from './steps/PaymentTypeStep';
import GatewayStep from './steps/GatewayStep';
import BankTransferStep from './steps/BankTransferStep';
import ConfirmationStep from './steps/ConfirmationStep';
import SuccessScreen from './components/SuccessScreen';

const EnhancedWalletTopup = () => {
  const navigate = useNavigate();
  const { isMockDataEnabled } = useMockData();
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // NGenius platform-fee config (percentage + fixed), fetched from the backend.
  const [gatewayConfig, setGatewayConfig] = useState({ feePercentage: 0, feeFixed: 0 });

  // Initialize mock completed tickets when component mounts and mock is enabled
  useEffect(() => {
    if (isMockDataEnabled) {
      addMockCompletedTickets();
    }
  }, [isMockDataEnabled]);

  // Load the NGenius platform-fee config so we can show the surcharge before payment.
  useEffect(() => {
    let active = true;
    AppService.get(API_ENDPOINTS.PAYMENT_GATEWAYS.ENABLED)
      .then((list) => {
        if (!active) return;
        const ng = (Array.isArray(list) ? list : []).find((g) => g.gatewayKey === 'NGENIUS');
        if (ng) {
          setGatewayConfig({
            feePercentage: Number(ng.feePercentage) || 0,
            feeFixed: Number(ng.feeFixed) || 0,
          });
        }
      })
      .catch(() => { /* no config row → no fee */ });
    return () => { active = false; };
  }, []);

  // Form Data
  const [formData, setFormData] = useState({
    amount: '',
    paymentType: '', // 'GATEWAY' or 'OFFLINE'

    // Gateway fields
    selectedGateway: null,
    gatewayFee: 0,
    netAmount: 0,

    // Offline payment fields
    paymentMethod: '', // 'BANK_TRANSFER' or 'CASH_DEPOSIT'
    selectedBankAccount: null,
    referenceNumber: '',
    receipt: null,
    customerNotes: '',
  });

  const steps = ['Enter Amount', 'Payment Method', 'Payment Details', 'Confirm'];

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setError('');
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0: // Amount
        const numAmount = parseFloat(formData.amount);
        if (!formData.amount || numAmount <= 0) {
          setError('Please enter a valid amount');
          return false;
        }
        if (numAmount < 10) {
          setError('Minimum topup amount is AED 10');
          return false;
        }
        if (numAmount > 100000) {
          setError('Maximum topup amount is AED 100,000');
          return false;
        }
        break;

      case 1: // Payment Type
        if (!formData.paymentType) {
          setError('Please select a payment method');
          return false;
        }
        break;

      case 2: // Payment Details
        if (formData.paymentType === 'GATEWAY') {
          if (!formData.selectedGateway) {
            setError('Please select a payment gateway');
            return false;
          }
        } else if (formData.paymentType === 'OFFLINE') {
          if (!formData.paymentMethod) {
            setError('Please select payment method (Bank Transfer or Cash Deposit)');
            return false;
          }
          if (!formData.selectedBankAccount) {
            setError('Please select a Thaklis bank account');
            return false;
          }
          if (!formData.referenceNumber || formData.referenceNumber.trim().length === 0) {
            setError('Please enter your bank reference number');
            return false;
          }
          if (!formData.receipt) {
            setError('Please upload your payment receipt or deposit slip');
            return false;
          }
        }
        break;

      default:
        break;
    }

    setError('');
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) {
      return;
    }

    // For GATEWAY at step 1, auto-select NGenius and skip to confirmation (step 3).
    // The platform fee is a surcharge ON TOP of the amount: the customer is charged
    // amount + fee; the wallet still receives the full amount (the fee is captured by
    // the gateway). The backend recomputes the authoritative fee on submit.
    if (activeStep === 1 && formData.paymentType === 'GATEWAY') {
      const amount = parseFloat(formData.amount);
      // Mirror the backend rounding: round the percentage part to 2dp, then add fixed.
      const pctPart = Math.round((amount * (gatewayConfig.feePercentage || 0)) / 100 * 100) / 100;
      const fee = Math.round((pctPart + (gatewayConfig.feeFixed || 0)) * 100) / 100;
      updateFormData({
        selectedGateway: {
          id: 'ngenius',
          name: 'NGenius',
          feePercentage: gatewayConfig.feePercentage,
          feeFixed: gatewayConfig.feeFixed,
        },
        gatewayFee: fee,
        netAmount: amount,          // wallet receives the full amount
        totalToPay: amount + fee,   // customer is charged amount + platform fee
      });
      setActiveStep(3); // Skip to confirmation
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    setError('');

    try {
      if (formData.paymentType === 'GATEWAY') {
        // Gateway payment flow — will redirect, don't set success state
        await processGatewayPayment();
        // If we reach here without redirect, keep processing state
        return;
      } else {
        // Offline payment flow
        await processOfflinePayment();
      }

      setSuccess(true);
      setIsProcessing(false);

      // Redirect after success
      setTimeout(() => {
        navigate('/account/wallet');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Payment processing failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const processGatewayPayment = async () => {
    if (isMockDataEnabled) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const request = addGatewayTopupRequest(formData);
      console.log('Mock gateway payment processed:', request);
      setSuccess(true);
      setIsProcessing(false);
      return;
    }

    const gateway = formData.paymentGateway || 'NGENIUS';
    console.log('[EnhancedWalletTopup] Creating online topup order:', {
      amount: parseFloat(formData.amount),
      gateway,
    });

    const response = await WalletDepositAPI.createOnlineTopup({
      amount: parseFloat(formData.amount),
    }, gateway);

    if (response.paymentUrl) {
      console.log('[EnhancedWalletTopup] Redirecting to payment page');
      window.location.href = response.paymentUrl;
    } else {
      throw new Error('No payment URL received from server');
    }
  };

  const processOfflinePayment = async () => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    if (isMockDataEnabled) {
      // Use mock data manager to add the offline topup request
      const request = addOfflineTopupRequest(formData);
      console.log('Mock offline payment request created:', request);
    } else {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('amount', formData.amount);
      submitData.append('paymentType', formData.paymentType);
      submitData.append('paymentMethod', formData.paymentMethod);
      submitData.append('selectedBankAccountId', formData.selectedBankAccount.id);
      submitData.append('referenceNumber', formData.referenceNumber);
      submitData.append('receipt', formData.receipt);
      if (formData.customerNotes) {
        submitData.append('customerNotes', formData.customerNotes);
      }

      console.log('Processing offline payment:', {
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        bankAccount: formData.selectedBankAccount,
        referenceNumber: formData.referenceNumber,
        receiptFile: formData.receipt.name,
        notes: formData.customerNotes
      });

      // In real implementation:
      // POST /v1/wallet/topup/create
      // with FormData containing all fields + file
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <AmountStep
            amount={formData.amount}
            onAmountChange={(amount) => updateFormData({ amount })}
          />
        );

      case 1:
        return (
          <PaymentTypeStep
            selectedType={formData.paymentType}
            onTypeSelect={(paymentType) => {
              updateFormData({
                paymentType,
                // Reset type-specific fields when switching
                selectedGateway: null,
                paymentMethod: '',
                selectedBankAccount: null,
                referenceNumber: '',
                receipt: null,
              });
            }}
          />
        );

      case 2:
        if (formData.paymentType === 'GATEWAY') {
          return (
            <GatewayStep
              amount={parseFloat(formData.amount) || 0}
              selectedGateway={formData.selectedGateway}
              onGatewaySelect={(gateway) => {
                const amount = parseFloat(formData.amount);
                const fee = (amount * gateway.feePercentage / 100) + gateway.feeFixed;
                const net = amount - fee;

                updateFormData({
                  selectedGateway: gateway,
                  gatewayFee: fee,
                  netAmount: net
                });
              }}
            />
          );
        } else {
          return (
            <BankTransferStep
              paymentMethod={formData.paymentMethod}
              selectedBankAccount={formData.selectedBankAccount}
              referenceNumber={formData.referenceNumber}
              receipt={formData.receipt}
              customerNotes={formData.customerNotes}
              onPaymentMethodChange={(method) => updateFormData({ paymentMethod: method })}
              onBankAccountSelect={(account) => updateFormData({ selectedBankAccount: account })}
              onReferenceNumberChange={(ref) => updateFormData({ referenceNumber: ref })}
              onReceiptUpload={(file) => updateFormData({ receipt: file })}
              onNotesChange={(notes) => updateFormData({ customerNotes: notes })}
            />
          );
        }

      case 3:
        return (
          <ConfirmationStep
            formData={formData}
            onBankAccountSelect={(account) => updateFormData({ selectedBankAccount: account })}
          />
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <SuccessScreen
        paymentType={formData.paymentType}
        amount={formData.amount}
        netAmount={formData.paymentType === 'GATEWAY' ? formData.netAmount : formData.amount}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Topup Wallet
      </Typography>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ minHeight: 400 }}>
            {renderStepContent()}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Button
              disabled={activeStep === 0 || isProcessing}
              onClick={handleBack}
              startIcon={<ArrowBack />}
            >
              Back
            </Button>

            <Box>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  size="large"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  disabled={isProcessing}
                  startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckCircle />}
                  size="large"
                >
                  {isProcessing ? 'Processing...' : 'Submit Request'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnhancedWalletTopup;
